"use client";

import { X } from "lucide-react";
import { useState } from "react";

import { ComposerInput } from "./ComposerInput";
import { ComposerToolbar } from "./ComposerToolbar";
import { HOME_GREETING } from "@/features/home/data/mock";
import {
  DEFAULT_COMPOSER_MODE,
  getComposerMode,
  type ComposerMode,
  type ComposerModeId,
} from "./modes";
import { cn } from "@/shared/lib/utils/cn";

interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  onSubmit?: (value: string, mode: ComposerMode) => void;
  onAttachFiles?: (files: FileList) => void;
}

export function ChatComposer({
  value,
  onChange,
  placeholder = HOME_GREETING.composerPlaceholder,
  disabled = false,
  onSubmit,
  onAttachFiles,
}: ChatComposerProps) {
  const [modeId, setModeId] = useState<ComposerModeId>(DEFAULT_COMPOSER_MODE.id);
  const [attachments, setAttachments] = useState<File[]>([]);
  const canSend = value.trim().length > 0 && !disabled;

  function submit() {
    if (!canSend) return;
    onSubmit?.(value.trim(), getComposerMode(modeId));
    onChange("");
    setAttachments([]);
  }

  function attach(files: FileList) {
    setAttachments((prev) => [...prev, ...Array.from(files)]);
    onAttachFiles?.(files);
  }

  return (
    <div
      data-testid="chat-composer"
      className={cn(
        "flex flex-col overflow-hidden rounded-2xl border border-border/70 bg-card transition-all",
        "shadow-[0_4px_24px_rgba(0,0,0,0.07),0_1px_4px_rgba(0,0,0,0.04)]",
        "focus-within:border-[color-mix(in_srgb,var(--spark-orange)_35%,var(--primary))] focus-within:shadow-[0_4px_28px_color-mix(in_srgb,var(--breeze-blue)_10%,transparent),0_0_24px_color-mix(in_srgb,var(--logo-ai-from)_12%,transparent),0_1px_4px_rgba(0,0,0,0.05)]",
      )}
    >
      {attachments.length > 0 ? (
        <div className="flex flex-wrap gap-2 border-b border-border/40 px-4 pt-3 pb-2">
          {attachments.map((file, index) => (
            <span
              key={`${file.name}-${index}`}
              className="inline-flex max-w-[200px] items-center gap-1.5 rounded-md bg-muted/60 px-2 py-1 text-[11px] text-foreground/80"
            >
              <span className="truncate">{file.name}</span>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                className="shrink-0 text-muted-foreground hover:text-foreground"
                onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== index))}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <ComposerInput
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        onSubmit={submit}
      />

      <ComposerToolbar
        canSend={canSend}
        selectedModeId={modeId}
        onModeChange={(mode) => setModeId(mode.id)}
        onAttachFiles={attach}
        onSend={submit}
      />
    </div>
  );
}
